<template>
<div class="nt-form">
  <form v-if="!posted" class="nt-form" novalidate="true" :key="key" :id="id" :action="urlAction" method="POST" @submit="onSubmit">
    <slot />
  </form>
  <div v-else :class="['nt-form__success', { 'nt-form__success--open': posted }]" @click="closeSuccessDialog">
    <div class="content">
      <div>
        {{ successMessage }}
      </div>
      <div class="nt-form__success--actions">
        <a :href="urlBack" id="btn-back" class="button button--default" v-if="urlBack">{{ buttonBack }}</a>
        <a id="btn-download-pdf" class="button button--default" :href="pdf" download v-if="pdf && !sendFileByEmail">
          <span class="icon">
            <svg>
              <use xlink:href="#download" />
            </svg>
          </span>
          {{ buttonDownload }}
        </a>

        <a id="btn-url-to-open" 
          :target="linkTarget" 
          class="button button--default" 
          :href="linkToOpen" 
          v-if="linkToOpen">
          {{ buttonOpenLinkText }}
        </a>
        <button id="btn-ok" class="button button--default" v-if="displayConfirm" @click="closeSuccessDialog">{{ buttonConfirm }}</button>
      </div>
      <div
        v-if="pdf && sendFileByEmail && fileSentByEmailMessage"
        class="nt-form__actionMessage nt-form__actionMessage--success"
        role="alert">
        <p> {{ fileSentByEmailMessage }} </p>
      </div>
    </div>
  </div>
</div>

</template>

<script>
import qs from 'qs'
import axios from 'axios'
import _ from 'lodash'
import utils from '../../../utils/utils'
import { mapActions } from 'vuex'
import countryList from '../../../json/countries.json'

const countries = countryList

const getCountryName = abbr => {
  const country = countries.find(c => c.abbreviation === abbr)
  return country ? country.name : abbr
}

export default {
  name: 'nt-form-weblead',
  props: {
    id: {
      type: String,
      default: 'form-lead'
    },
    errorMessages: {
      type: Object
    },
    urlAction: {
      type: String
    },
    successMessage: {
      type: String,
      default: 'Success'
    },
    buttonBack: {
      type: String,
      default: 'Back'
    },
    urlBack: {
      type: String
    },
    displayConfirm: {
      type: Boolean,
      default: false,
    },
    buttonConfirm: {
      type: String,
      default: 'Ok'
    },
    pdf: {
      type: String
    },
    sendFileByEmail: {
      type: Boolean
    },
    fileSentByEmailMessage: {
      type: String
    },
    buttonDownload: {
      type: String,
      default: 'Click here to download'
    },
    customResult: {
      type: Boolean,
      default: false
    },
    oneTrustUrl: {
      type: String,
      default: '/api/sitecore/nt/onetrust'
    },
    linkToOpen: {
      type: String,
      default: ''
    },
    linkTarget: {
      type: String,
      default: '_blank'
    },
    buttonOpenLinkText: {
      type: String,
      default: ''
    },
    autoRedirect: {
      type: Boolean,
      default: false
    },
    emailValidationApiUrl: {
      type: String,
      default: '/api/sitecore/emailvalidation'
    },
    enableEmailValidation: {
      type: Boolean,
    },
    taggingFields: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      labelValues: {},
      formData: {},
      validators: {},
      errors: [],
      key: 0,
      posted: false,
      invalid: true,
      queryStringSalesForce: null,
      hasSalesForceSuccess: false,
      localStorageForm: null,
      gtmData: {}
    }
  },
  watch: {
    errors(values) {
      this.$nextTick(() => {
        this.focusInError(values)
      })
    },
    formData: {
      handler() {
        this.validateForm(this.formData)
      },
      deep: true,
    },
  },
  created() {
    this.$on('changeForm', this.onChangeForm)
    this.$on('storeRealValue', this.storeRealValue)
    this.$on('field-blur', this.validateField)

    Object.assign(this.formData, {
      form: this.id
    })
  },
  mounted() {
    this.localStorageForm = window.localStorage.getItem('posted-weblead')
    this.queryStringSalesForce = new URLSearchParams(window.location.search)

    if (this.queryStringSalesForce && this.queryStringSalesForce.get('success') && this.localStorageForm) {
      const data = qs.parse(this.localStorageForm)
      if (data.form !== this.formData.form) return
      this.posted = true
      if (this.linkToOpen && this.autoRedirect) {
        setTimeout(() => {
          window.open(this.linkToOpen, this.linkTarget)
        }, 1000)
      }
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
      axios.post(this.oneTrustUrl, this.localStorageForm, config)
      window.localStorage.removeItem('posted-weblead')
      setTimeout(() => {
        this.scrollToSuccessModal()
      }, 200)
    }
    this.recaptchaCheckInterval = setInterval(() => {
      if (window.grecaptcha && window.grecaptcha.getResponse) {
        try {
          this.validateForm(this.formData)
        } catch (e) {
          if (e instanceof Error && e.message === 'No reCAPTCHA clients exist.') {
            clearInterval(this.recaptchaCheckInterval)
          } else {
            console.warn('Erro inesperado ao verificar reCAPTCHA:', e)
          }
        }
      } else {
        clearInterval(this.recaptchaCheckInterval)
      }
    }, 1000)

    this.$emit('on-change-form', this.formData)
    this.observeFormView()
  },
  beforeDestroy() {
    if (this.recaptchaCheckInterval) {
      clearInterval(this.recaptchaCheckInterval)
    }
    if (this.formViewObserver) {
      this.formViewObserver.disconnect()
    }
  },
  methods: {
    ...mapActions([
      'changeLoading'
    ]),
    scrollToSuccessModal() {
      const formSuccessElment = document.querySelector('.nt-form__success')
      formSuccessElment.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
    },
    onChangeForm(val) {
      Object.assign(this.formData, val)
      this.$emit('on-change-form', this.formData)
      this.validateForm(this.formData)
    },
    storeRealValue(val) {
      Object.assign(this.labelValues, val)
    },
    reload() {
      this.key = Math.floor(Math.random() * 100)
    },
    validateProperty(property, validations) {
      const prop = this.formData[property]

      _.each(validations, (validation) => {
        switch (validation) {
          case 'email':
            this.checkEmail(prop, property, validation)
            break
          case 'required':
            this.checkRequired(prop, property, validation)
            break
          case 'recaptcha':
            this.checkRecaptcha(property, validation)
            break
          default:
            break
        }
      })
    },
    checkRecaptcha(property, validation) {
      if (window.grecaptcha.getResponse()) return

      this.errors.push({
        [property]: this.errorMessages[validation]
      })
    },
    checkRequired(val, property, validation) {
      if (val) return

      this.errors.push({
        [property]: this.errorMessages[validation]
      })
    },
    checkEmail(val, property, validation) {
      if (val && utils.isValidEmail(val)) return

      this.errors.push({
        [property]: this.errorMessages[validation]
      })
    },
    isValidForm() {
      this.errors = []

      _.each(this.validators, (validations, property) => {
        this.validateProperty(property, validations)
      })

      const isFormValid = !this.errors.length

      return isFormValid
    },
    focusInError(values) {
      if (!values) return

      const key = _.findKey(_.get(values, 0))
      const element = document.getElementById(key)

      if (!element) return

      const topPos = element.getBoundingClientRect().top + window.scrollY - 206

      document.body.scrollTop = topPos // For Safari
      document.documentElement.scrollTop = topPos // For Chrome, Firefox, IE and Opera
    },
    observeFormView() {
      if (!('IntersectionObserver' in window) || this.posted) return

      this.formViewObserver = new IntersectionObserver((entries) => {
        const isVisible = entries.some(entry => entry.isIntersecting)
        if (!isVisible) return

        this.pushFormViewGTM()
        this.formViewObserver.disconnect()
      }, { threshold: 0.1 })

      this.formViewObserver.observe(this.$el)
    },
    pushFormViewGTM() {
      if (!window.dataLayer) {
        console.warn('DataLayer not found.')
        return
      }

      window.dataLayer.push({
        event: 'form_view',
        form_name: this.taggingFields && this.taggingFields.form_name ? this.taggingFields.form_name : '',
        section: this.pdf ? 'download' : 'contato'
      })
    },
    pushGTM() {
      if (!window.dataLayer) {
        console.warn('DataLayer not found.')
        return
      }

      Object.keys(this.taggingFields).forEach((key) => {
        const formFieldName = this.taggingFields[key]
        let value = this.formData[formFieldName]

        if (key === 'country') {
          value = getCountryName(value)
        }

        if (value !== undefined && value !== null) {
          if (key === 'mkt_comms') {
            this.gtmData[key] = value === 1 || value === '1' ? 'sim' : 'não'
          } else {
            this.gtmData[key] = value
          }
        }
      })

      const fromPopup = sessionStorage.getItem('nt-scroll-popup-cta') === 'yes'
      if (fromPopup) sessionStorage.removeItem('nt-scroll-popup-cta')

      window.dataLayer.push({
        event: 'lead_generated',
        form_name: this.taggingFields && this.taggingFields.form_name ? this.taggingFields.form_name : '',
        method: fromPopup ? 'yes' : 'no',
        ...this.gtmData
      })
    },
    onSubmit(event) {
      event.preventDefault()

      if (!this.isValidForm()) {
        return
      }

      const email = this.formData.email
      this.changeLoading(true)

      if (this.enableEmailValidation) {
        this.validateEmailApi(email)
        .then(validationResult => {
          if (validationResult.valid) {
            window.localStorage.setItem('posted-weblead', qs.stringify(this.formData, { arrayFormat: 'brackets' }))
            this.pushGTM()
            event.target.submit()
          } else {
            this.errors.push({ email: validationResult.message })
          }
        })
        .catch(() => {
          this.errors.push({ email: this.errorMessages['invalid-email'] })
        })
        .finally(() => {
          this.changeLoading(false)
        })
      } else {
        window.localStorage.setItem('posted-weblead', qs.stringify(this.formData, { arrayFormat: 'brackets' }))
        this.pushGTM()
        event.target.submit()
      }
    },
    closeSuccessDialog() {
      this.posted = false
    },
    validateForm(formData) {
      if (this.posted) return

      const submitButton = document.querySelector('#btn-submit')

      if (submitButton) {
        const hasRequiredFieldsEmptyOrNull = Object.entries(formData).some(
          ([key, value]) => {
            const isRequired =
              this.validators &&
              this.validators[key] &&
              this.validators[key].includes('required')
            return isRequired && !value
          }
        )

        const requiresRecaptcha = Object.values(this.validators).some((v) =>
          v.includes('recaptcha')
        )
        const isRecaptchaMarked =
          !requiresRecaptcha ||
          (window.grecaptcha && window.grecaptcha.getResponse())

        const isFormInvalid =
          hasRequiredFieldsEmptyOrNull || !isRecaptchaMarked

        submitButton.disabled = isFormInvalid
        submitButton.classList.toggle('button--disabled', isFormInvalid)
      }
    },
    validateEmailApi(email) {
      return axios.post(this.emailValidationApiUrl, { email })
    .then(response => {
      if (response.status === 200) {
        return { valid: true }
      }
      return { valid: false }
    })
    .catch(error => {
      if (error.response && error.response.status === 400) {
        const message = error.response.data.message
        return { valid: false, message }
      }
      return { valid: false }
    })
    },
    validateField(field) {
      if (!Array.isArray(this.errors)) this.errors = []

      this.errors = this.errors.filter(err => !err[field])

      if (this.validators && this.validators[field]) {
        this.validateProperty(field, this.validators[field])
      }

      this.$forceUpdate()
    },
  }
}
</script>
